import React, { useContext, useMemo } from 'react';
import { isFunction, noop } from 'underscore';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withFinalUrlRecommendationUpdate } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import {
  Collapse,
  getPanelErrorProps,
  TextTitle,
  ValidatedFixedPanel,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { entityIs, andComp, propertyIs } from '@bingads-webui-campaign-react/form-error';

import { ValidatedFinalUrlWithRecommendations } from './validated-final-url-with-recommendations';
import FinalUrlImg from '../media/final-url.svg';

const getServerValidatedFinalUrl = (entity) => {
  const finalUrlsProp = andComp(entityIs(entity), propertyIs('FinalUrls'));
  const ValidatedFinalUrlWithStoreURL = withError(withCustomValidation(withFinalUrlRecommendationUpdate(ValidatedFinalUrlWithRecommendations)));
  const ServerValidatedFinalUrl = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedFinalUrlWithStoreURL, { filter: finalUrlsProp }));
  return ServerValidatedFinalUrl;
};

const finalUrl = 'final-url';
const finalUrlSection = [finalUrl];

export const ValidatedFinalUrlWithRecommendationsSection = (props) => {
  const {
    customUrlProtocol,
    finalUrls,
    onFinalURLFocusOut,
    onFinalURLEnterKeyUp,
    entity,
    enableCopilotRecommendations,
    enableUrlRecommendation,
  } = props;

  const i18n = useContext(I18nContext);
  const ServerValidatedFinalUrl = useMemo(() => getServerValidatedFinalUrl(entity), [entity]);

  const finalUrlValidationProps = useMemo(() => ({
    data: {
      FinalUrls: finalUrls,
    },
    initialValue: {
      FinalUrls: finalUrls,
    },
    debounce: 700,
  }), [finalUrls]);

  const onKeyUp = useMemo(() => {
    if (!isFunction(onFinalURLEnterKeyUp)) {
      return undefined;
    }

    return (keyboardEvent) => {
      if (keyboardEvent.key === 'Enter') {
        onFinalURLEnterKeyUp();
      }
    };
  }, [onFinalURLEnterKeyUp]);

  return (
    <Collapse allPanelItems={finalUrlSection} checkItems skipItemsTypeCheck activeKeys={finalUrlSection} setActiveKeys={noop}>
      <ValidatedFixedPanel
        key="final-url"
        title={<TextTitle titleKey={_TL_('Final URL')} topicKey="pop_BA_NewAdCreate_FinalURL" icon={FinalUrlImg} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <div className="final-url-container">
          <ServerValidatedFinalUrl
            i18n={i18n}
            validationProps={finalUrlValidationProps}
            enableUrlRecommendation={enableUrlRecommendation}
            enableCopilotRecommendations={enableCopilotRecommendations}
            validateOnRender
            isDisplayPlus
            additionalProperties={{ onBlur: onFinalURLFocusOut, onKeyUp }}
            customUrlProtocol={customUrlProtocol}
          />
        </div>
      </ValidatedFixedPanel>
    </Collapse>
  );
};

ValidatedFinalUrlWithRecommendationsSection.propTypes = {
  entity: PropTypes.string,
  onFinalURLFocusOut: PropTypes.func.isRequired,
  onFinalURLEnterKeyUp: PropTypes.func,
  permissions: PropTypes.shape({}).isRequired,
  finalUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
  customUrlProtocol: PropTypes.bool,
  enableUrlRecommendation: PropTypes.bool,
  enableCopilotRecommendations: PropTypes.bool,
};

ValidatedFinalUrlWithRecommendationsSection.defaultProps = {
  customUrlProtocol: false,
  entity: 'ResponsiveAd',
  enableUrlRecommendation: true,
  enableCopilotRecommendations: true,
  onFinalURLEnterKeyUp: undefined,
};
